import { Condition } from 'src/components/Filters/DIConditionInputFilter';
import {
	ChartPeriod,
	FilterOperation,
	ChartType,
	ReportsMetaDataResponse,
	ReportConfig,
} from 'src/types/reportingTypes';
import { DateTime } from 'luxon';

export function caseCloudTypeInfo(cloudType: string): string {
	let connectorText;
	switch (cloudType.toLowerCase()) {
		case 'onedriveconnector':
		case 'onedrive':
			connectorText = 'OneDrive';
			break;
		case 'sharepointconnector':
		case 'sharepoint':
		case 'sharepointlibraryconnector':
			connectorText = 'Sharepoint';
			break;

		case 'azurefilesconnector':
			connectorText = 'Azure';
			break;
		default:
			connectorText = 'Unknown';
	}
	return connectorText;
}

export const formatWithOptions = (
	operations: FilterOperation[],
): Condition[] => {
	const operationLabels: Record<FilterOperation, string> = {
		[FilterOperation.CONTAINS]: 'Contains',
		[FilterOperation.NOT_CONTAINS]: 'Does not contain',
		[FilterOperation.BLANK]: 'Is blank',
		[FilterOperation.NOT_BLANK]: 'Is not blank',
		[FilterOperation.EQUALS]: 'Is equal to',
		[FilterOperation.GREATER]: 'After',
		[FilterOperation.BETWEEN]: 'Between',
		[FilterOperation.LESS]: 'Before',
		[FilterOperation.DATE_EQUALS]: 'Is equal to',
		[FilterOperation.IN]: 'Is in',
	};

	return operations.map((operation) => ({
		label: operationLabels[operation],
		value: operation,
	}));
};

export const getDateRangeFromRelative = (
	relative: string,
): [DateTime, DateTime] | [null, null] => {
	const now = DateTime.local();
	switch (relative) {
		case 'LAST_7_DAYS':
			return [now.minus({ days: 7 }).startOf('day'), now];
		case 'LAST_30_DAYS':
			return [now.minus({ days: 30 }).startOf('day'), now];
		case 'LAST_60_DAYS':
			return [now.minus({ days: 60 }).startOf('day'), now];
		case 'LAST_90_DAYS':
			return [now.minus({ days: 90 }).startOf('day'), now];
		case 'YESTERDAY':
			return [
				now.minus({ days: 1 }).startOf('day'),
				now.minus({ days: 1 }).endOf('day'),
			];
		case 'TODAY':
			return [now.startOf('day'), now];
		default:
			return [null, null];
	}
};

export const getChartPeriodFromDateRange = (
	dateRange: [DateTime | null, DateTime | null],
	chartPeriod?: ChartPeriod,
): ChartPeriod | null => {
	const [start, end] = dateRange;
	if (!start || !end) return null;

	const diffDays =
		end.startOf('day').diff(start.startOf('day'), 'days').days + 1;

	if (diffDays <= 1) {
		return ChartPeriod.HOURLY;
	} else if (diffDays <= 8) {
		return ChartPeriod.DAILY;
	} else if (diffDays <= 31) {
		return chartPeriod !== ChartPeriod.MONTHLY &&
			chartPeriod !== ChartPeriod.HOURLY
			? chartPeriod
			: ChartPeriod.DAILY;
	} else {
		return chartPeriod !== ChartPeriod.HOURLY
			? chartPeriod
			: ChartPeriod.DAILY;
	}
};

export const getTruncatedReportName = (name: string, maxLength = 20): string =>
	name.length > maxLength ? name.slice(0, maxLength) + '…' : name;

export const getChartColors = (datasetCount: number, tokens: any): string[] => {
	return Array.from({ length: datasetCount }, (_, i) => {
		const seriesNum = (i % 8) + 1;
		const tokenName = `chartsColorSeries${seriesNum}Range100`;
		return tokens[tokenName] || tokens.chartsColorSeries1Range100;
	});
};

// Helper function to check if a string is a valid date
export const isValidDate = (dateString: string) => {
	const date = new Date(dateString);
	return !isNaN(date.getTime());
};

export function getValidChartType(config: ReportConfig): ReportConfig {
	const groupByCount = config.groupBy?.length ?? 0;
	let chartType = config.chartType;

	switch (groupByCount) {
		case 0:
			if (
				!(
					chartType === ChartType.LINE ||
					chartType === ChartType.VERTICAL_BAR
				)
			) {
				chartType = ChartType.LINE;
			}
			break;
		case 1:
			if (
				!(
					chartType === ChartType.HORIZONTAL_BAR ||
					chartType === ChartType.TABLE
				)
			) {
				chartType = ChartType.HORIZONTAL_BAR;
			}
			break;
		case 2:
			if (
				!(
					chartType === ChartType.HORIZONTAL_STACKED_BAR ||
					chartType === ChartType.HORIZONTAL_GROUPED_BAR
				)
			) {
				chartType = ChartType.HORIZONTAL_STACKED_BAR;
			}
			break;
		default:
			// No change for other cases
			break;
	}

	return {
		...config,
		chartType,
	};
}

// Main transformer for the payload
export function transformReportsMetaDataPayload(
	payload: ReportsMetaDataResponse,
): ReportsMetaDataResponse {
	if (!payload?.reports?.reports) return payload;

	const transformedReports = payload.reports.reports.map((report) => ({
		...report,
		config: getValidChartType(report.config),
	}));

	return {
		...payload,
		reports: {
			...payload.reports,
			reports: transformedReports,
		},
	};
}
